import React, { useState, useMemo, memo, useEffect, useLayoutEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import { formatNumber, getExchangeLogo } from '../../utils/helpers';
import { useSoundNotificationsContext } from '../../contexts/SoundNotificationsContext';
import { useTopTrades } from '../../hooks/useTopTrades';
import { trackTradeInteraction, trackFeatureUsage, trackModalOpen, trackCoinClick } from '../../utils/analytics';
import LiquidationSummaryModal from '../LiquidationSummaryModal/LiquidationSummaryModal';
import TradePopup from '../TradePopup/TradePopup';
import TradeTooltip from '../TradeTooltip/TradeTooltip';
import ShareModal from '../ShareModal/ShareModal';
import SoundToggle from '../SoundToggle/SoundToggle';
import SkeletonTradeRow from '../SkeletonTradeRow/SkeletonTradeRow';
import { FILTER_DEFAULTS } from '../../hooks/useFilters';
import './LiquidationsTable.css';

// Random GIF backgrounds for liquidations (served locally)
const LIQUIDATION_GIFS_LONG = [
  '/assets/gifs/whale-effect-1.webp',
  '/assets/gifs/whale-effect-4.webp',
  '/assets/gifs/whale-effect-2.webp',
  '/assets/gifs/whale-effect-3.webp'
];

const LIQUIDATION_GIFS_SHORT = [
  '/assets/gifs/whale-effect-1.webp',
  '/assets/gifs/whale-effect-2.webp'
];

// Pick random GIF from array
function pickRandomGif(side) {
  const gifs = side === 'buy' ? LIQUIDATION_GIFS_LONG : LIQUIDATION_GIFS_SHORT;
  return gifs[Math.floor(Math.random() * gifs.length)];
}

// Helper to determine volume class and styling
const getVolumeClass = (trade) => {
  if (trade.side === 'sell') {
    if (trade.dollarSize >= 1000000) return 'volume-3 big';
    if (trade.dollarSize >= 100000) return 'volume-2';
    return 'volume-1';
  } else {
    if (trade.dollarSize >= 1000000) return 'volume-6 big';
    if (trade.dollarSize >= 100000) return 'volume-5';
    return 'volume-4';
  }
};

const getBackgroundClass = (trade) => {
  // Tier system completely replaces old black-bg/black-green classes
  // Return empty string for all trades with tier
  if (trade.tier) {
    return '';
  }

  // Only apply old classes for legacy trades without tier (< $1M)
  if (trade.dollarSize >= 1000000) {
    return trade.side === 'sell' ? 'black-bg' : 'black-green';
  }

  return '';
};

// Memoized liquidation row component to prevent unnecessary re-renders
const LiquidationRow = memo(({ trade, onClick, t }) => {
  const shouldAnimate = !trade.isHistorical && trade.tier >= 2;
  const isTier1WithGif = trade.tier === 1 && trade.dollarSize >= 5_000_000;
  const randomGif = trade.randomGif || (isTier1WithGif ? pickRandomGif(trade.side) : null);

  const tierClass = trade.tier >= 1 ? `tier-${trade.tier}` : '';
  const animateClass = shouldAnimate ? 'animate-tier' : '';
  const historicalClass = trade.isHistorical ? 'historical-trade' : '';
  const backgroundClass = getBackgroundClass(trade);

  const inlineStyle = (isTier1WithGif && randomGif) ? {
    backgroundImage: `url(${randomGif})`,
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center center'
  } : { backgroundImage: 'none' };

  // Liquidations: sell = LONG LIQ, buy = SHORT LIQ

  return (
    <a
      href="#"
      className={`position-item ${historicalClass} ${backgroundClass} ${tierClass} ${animateClass}`.trim()}
      style={inlineStyle}
      onClick={(e) => { e.preventDefault(); onClick(trade, e); }}
      data-price={trade.price}
      data-timestamp={trade.timestamp}
      data-symbol={trade.symbol}
      data-exchange={trade.exchange}
      data-side={trade.side}
      data-size={trade.dollarSize}
      data-is-liquidation="true"
      data-tier={trade.tier || ''}
    >
      {/* Static icons */}
      <div className="row-static">
        <img className="exchange" src={trade.exchangeLogo || getExchangeLogo(trade.exchange)} alt={trade.exchange} title={trade.exchange} />
        {trade.logoUrl && (
          <img className="coin fade-in" src={trade.logoUrl} alt={trade.symbol} title={trade.symbol} onError={(e) => { e.target.style.display = 'none'; }} />
        )}
      </div>

      {/* Content */}
      <div className="row-content">
        <div className="row-front">
          <p className="row-coin-name">{trade.baseCoin}</p>
          <div className={`volume v2 ${getVolumeClass(trade)}`}>
            <span className="count">{trade.emoji} ${formatNumber(trade.dollarSize)}</span>
          </div>
        </div>
      </div>
    </a>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.trade.exchange === nextProps.trade.exchange &&
    prevProps.trade.symbol === nextProps.trade.symbol &&
    prevProps.trade.timestamp === nextProps.trade.timestamp &&
    prevProps.trade.price === nextProps.trade.price &&
    prevProps.trade.tier === nextProps.trade.tier &&
    prevProps.trade.logoUrl === nextProps.trade.logoUrl
  );
});

LiquidationRow.displayName = 'LiquidationRow';

function LiquidationsTable({ liquidations, loading, summary, prices, formatPrice, onOpenChart, selectedCoins, filters, registerCloseTooltip, onOtherTableClick }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { localizedPath } = useLanguage();
  // Sound notifications hook
  const { liquidationsEnabled, toggleLiquidationsSound } = useSoundNotificationsContext();

  // Fetch top 3 liquidations from entire database (once on page load)
  const { topTrades: top3Liquidations } = useTopTrades('liquidation', 3);

  const [showSummary, setShowSummary] = useState(false);
  const [selectedTrade, setSelectedTrade] = useState(null);
  const [tooltipAnchor, setTooltipAnchor] = useState(null);
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
  const [lastTradeTime, setLastTradeTime] = useState(null);
  const lastUpdateRef = useRef(null);
  const [shareModalData, setShareModalData] = useState(null);
  const [showTop3, setShowTop3] = useState(false);
  const top3ButtonRef = useRef(null);
  const infoHoverRef = useRef(null);

  // Hide tooltip and top3 dropdown on page scroll (mobile fix)
  // Only react to page-level scroll, not internal container scroll
  useEffect(() => {
    const handleScroll = (e) => {
      // Ignore scroll events from the trades scroll container
      if (scrollContainerRef.current && scrollContainerRef.current.contains(e.target)) {
        return;
      }
      // Hide info-hover tooltip
      if (infoHoverRef.current) {
        infoHoverRef.current.blur();
        infoHoverRef.current.style.pointerEvents = 'none';
        setTimeout(() => {
          if (infoHoverRef.current) {
            infoHoverRef.current.style.pointerEvents = '';
          }
        }, 100);
      }
      // Close top3 dropdown
      setShowTop3(false);
    };

    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, []);

  // Scroll management
  const scrollContainerRef = useRef(null);
  const [isAtTop, setIsAtTop] = useState(true);
  const isAtTopRef = useRef(true); // Ref to avoid stale state in scroll adjustment
  const prevLiquidationsLengthRef = useRef(liquidations.length);
  const prevScrollHeightRef = useRef(0);
  const scrollPositionRef = useRef(0);

  // Track scroll position and determine if user is at top
  // Attach ONCE on mount - do NOT re-attach on liquidations.length change
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const atTop = container.scrollTop < 50;
      scrollPositionRef.current = container.scrollTop;
      if (atTop !== isAtTopRef.current) {
        isAtTopRef.current = atTop;
        setIsAtTop(atTop);
      }
    };

    // Set initial state
    handleScroll();
    prevScrollHeightRef.current = container.scrollHeight;

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []); // Empty dependency - attach once on mount

  // Handle scroll position when new liquidations are added
  // Uses useLayoutEffect to run synchronously BEFORE browser paints (prevents jitter)
  useLayoutEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const newLiquidationsCount = liquidations.length - prevLiquidationsLengthRef.current;

    if (newLiquidationsCount > 0) {
      // Calculate how much content was added
      const newScrollHeight = container.scrollHeight;
      const heightAdded = newScrollHeight - prevScrollHeightRef.current;

      if (isAtTopRef.current) {
        // User is at top - keep them at top to see newest liquidations
        container.scrollTop = 0;
        scrollPositionRef.current = 0;
      } else if (heightAdded > 0) {
        // User is scrolled down - adjust scrollTop to keep same content visible
        // New liquidations push content down, so we need to add the height difference
        container.scrollTop = scrollPositionRef.current + heightAdded;
        scrollPositionRef.current = container.scrollTop;
      }
    }

    // Update refs for next comparison
    prevLiquidationsLengthRef.current = liquidations.length;
    prevScrollHeightRef.current = container.scrollHeight;
  }, [liquidations.length]); // Only run when liquidations count changes

  // Scroll to top function
  const scrollToTop = () => {
    const container = scrollContainerRef.current;
    if (container) {
      container.scrollTo({ top: 0, behavior: 'smooth' });
      setIsAtTop(true);
    }
  };

  // Enrich top 3 trades with baseCoin (logoUrl already loaded by useTopTrades hook)
  const enrichedTop3 = useMemo(() => {
    return top3Liquidations.map(trade => {
      const baseCoin = trade.symbol?.replace(/USDT$|USD$/i, '').toUpperCase() || '';
      return { ...trade, baseCoin };
    });
  }, [top3Liquidations]);

  // Calculate summary stats - liquidations have 'sell' for long liquidations and 'buy' for short liquidations
  const longLiquidations = useMemo(() => liquidations.filter(t => t.side === 'sell'), [liquidations]);
  const shortLiquidations = useMemo(() => liquidations.filter(t => t.side === 'buy'), [liquidations]);
  const totalVolume = useMemo(() => liquidations.reduce((sum, t) => sum + t.dollarSize, 0), [liquidations]);
  const longPercentage = liquidations.length > 0 ? Math.round((longLiquidations.length / liquidations.length) * 100) : 50;

  // Close top 3 dropdown when clicking outside
  useEffect(() => {
    if (!showTop3) return;

    const handleClickOutside = (event) => {
      if (top3ButtonRef.current && !top3ButtonRef.current.contains(event.target)) {
        setShowTop3(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showTop3]);

  // Close popup when clicking outside or scrolling
  useEffect(() => {
    if (!selectedTrade) return;

    const handleClickOutside = () => {
      setSelectedTrade(null);
    };

    const handleScroll = () => {
      setSelectedTrade(null);
    };

    document.addEventListener('click', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('click', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [selectedTrade]);

  // Update last trade time when new liquidations come in
  useEffect(() => {
    if (liquidations.length > 0) {
      setLastTradeTime(Date.now());
    }
  }, [liquidations]);

  // Update "last update" text every second via direct DOM write (avoids re-render)
  useEffect(() => {
    if (!lastTradeTime) {
      if (lastUpdateRef.current) lastUpdateRef.current.textContent = '-';
      return;
    }

    const updateText = () => {
      if (!lastUpdateRef.current) return;
      const now = Date.now();
      const diffMs = now - lastTradeTime;
      const diffSec = Math.floor(diffMs / 1000);

      let text;
      if (diffSec < 60) {
        text = t('time.secondsAgo', { count: diffSec });
      } else if (diffSec < 3600) {
        text = t('time.minutesAgo', { count: Math.floor(diffSec / 60) });
      } else {
        text = t('time.hoursAgo', { count: Math.floor(diffSec / 3600) });
      }
      lastUpdateRef.current.textContent = text;
    };

    updateText();
    const interval = setInterval(updateText, 1000);

    return () => clearInterval(interval);
  }, [lastTradeTime, t]);

  // Register close function with parent for cross-table tooltip management
  useEffect(() => {
    if (registerCloseTooltip) {
      registerCloseTooltip(() => setSelectedTrade(null));
    }
  }, [registerCloseTooltip]);

  const handleTradeClick = (trade, event) => {
    event.stopPropagation();
    const key = `${trade.exchange}-${trade.symbol}-${trade.timestamp}-${trade.price}`;

    trackTradeInteraction('view_details', {
      symbol: trade.symbol,
      exchange: trade.exchange,
      dollarSize: trade.dollarSize,
      side: trade.side,
      isLiquidation: true
    });

    if (onOtherTableClick) onOtherTableClick();

    const row = event.currentTarget;
    if (row) setTooltipAnchor(row.getBoundingClientRect());

    setSelectedTrade(prev => {
      const prevKey = prev ? `${prev.exchange}-${prev.symbol}-${prev.timestamp}-${prev.price}` : null;
      return prevKey === key ? null : trade;
    });
  };

  // Handle Live Chart button click
  const handleLiveChart = (symbol) => {
    if (onOpenChart) {
      onOpenChart(symbol);
    }
  };

  // Handle Share button click
  const handleShare = (trade, markPrice) => {
    setShareModalData({ trade, markPrice });
    setSelectedTrade(null); // Close the popup
  };

  // Handle Coin Details button click
  const handleCoinDetails = (symbol) => {
    trackCoinClick(symbol, {
      page: '/trades',
      source: 'trade_popup',
      trade_type: 'liquidation'
    });
    navigate(localizedPath(`/price/${symbol}`));
  };

  if (loading) {
    return (
      <div className="card">
        <div className="card-header d-flex align-items-center justify-content-between">
          <div className="card-title mb-0">
            <p>🔥 {t('liquidations.title')}</p>
          </div>
        </div>
        <SkeletonTradeRow />
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header d-flex align-items-end justify-content-between flex-nowrap">
        <div className="card-title mb-0 d-flex align-items-center">
          <div className="info-hover" ref={infoHoverRef}>
            <p className="title-with-tooltip">🔥 {t('liquidations.title')}</p>
            <div className="tooltip-card">
              <div className="card-body">
                <h5>{t('liquidations.tooltipTitle')}</h5>
                <span className="divider"></span>
                <p>{t('liquidations.tooltipDesc1')}</p>
                <p>{t('liquidations.tooltipDesc2')}</p>
                <h5>{t('liquidations.howToFollow')}</h5>
                <span className="divider"></span>
                <p className="mb-0"><span className="light">{t('liquidations.longLiquidation')}</span> → {t('liquidations.longLiquidationDesc')}</p>
                <p><span className="light">{t('liquidations.shortLiquidation')}</span> → {t('liquidations.shortLiquidationDesc')}</p>
                <span className="divider"></span>
                <p>{t('liquidations.tooltipCategorized')}</p>
                <p className="signal-title danger">{t('liquidations.longLiquidations')} 📉</p>
                <div className="signals mb-3">
                  <div className="signal-1">0-100K 🐟</div>
                  <div className="signal-2">100K-1M 🐠</div>
                  <div className="signal-3">1M+ 🐬</div>
                </div>
                <p className="signal-title success">{t('liquidations.shortLiquidations')} 📈</p>
                <div className="signals">
                  <div className="signal-4">0-100K 🐟</div>
                  <div className="signal-5">100K-1M 🐠</div>
                  <div className="signal-6">1M+ 🐬</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="d-flex align-items-center gap-2">
          <div className="header-actions-row">
            <SoundToggle
              enabled={liquidationsEnabled}
              onToggle={toggleLiquidationsSound}
              label={t('liquidations.title')}
              hideTooltipOnMobile={false}
              tooltipContent={`
                <h4>🔥 ${t('soundTiers.liquidationsTitle')}</h4>
                <div class="tier-info">
                  <div class="tier-row">
                    <span class="tier-label">${t('soundTiers.tier1')}:</span>
                    <span class="tier-value">$1M - $25M 🐟</span>
                  </div>
                  <div class="tier-row">
                    <span class="tier-label">${t('soundTiers.tier2')}:</span>
                    <span class="tier-value">$25M - $50M 🐠</span>
                  </div>
                  <div class="tier-row">
                    <span class="tier-label">${t('soundTiers.tier3')}:</span>
                    <span class="tier-value">$50M - $100M 🐬</span>
                  </div>
                  <div class="tier-row">
                    <span class="tier-label">${t('soundTiers.tier4')}:</span>
                    <span class="tier-value">$100M+ 🐋</span>
                  </div>
                </div>
              `}
            />
          <div
            ref={top3ButtonRef}
            style={{ position: 'relative' }}
          >
            <button
              className="header-action-btn-small"
              onClick={(e) => {
                e.stopPropagation();
                if (!showTop3) {
                  trackFeatureUsage('top3_liquidations', { page: '/trades' });
                }
                setShowTop3(!showTop3);
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"/>
              </svg>
            </button>
            {showTop3 && enrichedTop3.length > 0 && (
              <div className="top3-dropdown">
                {enrichedTop3.map((trade, index) => (
                  <div
                    key={`${trade.exchange}-${trade.symbol}-${trade.timestamp}`}
                    className="top3-dropdown-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowTop3(false);
                      handleTradeClick(trade, e);
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ color: '#888', fontSize: '12px', fontWeight: 'bold' }}>#{index + 1}</span>
                      {trade.logoUrl && (
                        <img
                          src={trade.logoUrl}
                          alt={trade.symbol}
                          style={{ width: '20px', height: '20px', borderRadius: '50%' }}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      )}
                      <span style={{ color: '#fff', fontSize: '12px', fontWeight: '500' }}>{trade.baseCoin}</span>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{
                        color: trade.side === 'sell' ? '#b43e3e' : '#3eb474',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}>
                        ${formatNumber(trade.dollarSize)}
                      </div>
                      <div style={{
                        color: trade.side === 'sell' ? '#b43e3e' : '#3eb474',
                        fontSize: '10px',
                        textTransform: 'uppercase'
                      }}>
                        {trade.side === 'sell' ? t('liquidations.long') : t('liquidations.short')}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
            <button
              className="header-action-btn-small"
              onClick={() => {
                trackFeatureUsage('liquidation_summary', { page: '/trades' });
                setShowSummary(true);
              }}
              title="View 24h Summary"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                <path d="M2 13H8V21H2V13ZM9 3H15V21H9V3ZM16 8H22V21H16V8Z"/>
              </svg>
            </button>
          </div>
          {!isAtTop && liquidations.length > 0 && (
            <button
              className="scroll-top-header-btn"
              onClick={scrollToTop}
              title="Scroll to top"
              style={{ flexShrink: 0 }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                <path d="M12 4l-8 8h5v8h6v-8h5z"/>
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Compact Summary Stats */}
      <div className="compact-summary" style={{ backgroundColor: '#1a1a1a', borderTop: '1px solid #333', padding: '8px 12px', fontSize: '11px' }}>
        <div className="d-flex justify-content-between align-items-center flex-nowrap">
          <div className="d-flex align-items-center flex-nowrap">
            <span className="text-danger me-2" style={{ fontWeight: 'bold' }}>{longLiquidations.length}</span>
            <span className="text-muted me-3 d-none d-sm-inline" style={{ fontSize: '10px' }}>{t('liquidations.long')}</span>
            <span className="text-success me-2" style={{ fontWeight: 'bold' }}>{shortLiquidations.length}</span>
            <span className="text-muted d-none d-sm-inline" style={{ fontSize: '10px' }}>{t('liquidations.short')}</span>
          </div>
          <div className="d-flex align-items-center flex-nowrap">
            <div className="progress me-2" style={{ height: '8px', width: '60px', backgroundColor: '#3eb474', borderRadius: '4px' }}>
              <div className="progress-bar" style={{ width: `${longPercentage}%`, backgroundColor: '#b43e3e', borderRadius: '4px' }}></div>
            </div>
            <small className="text-warning" style={{ fontWeight: 'bold' }}>${formatNumber(totalVolume)}</small>
            <small className="text-muted ms-2 d-none d-md-inline" style={{ fontSize: '9px' }}>{t('liquidations.last')}: <span ref={lastUpdateRef}>-</span></small>
          </div>
        </div>
      </div>

      <div className="card-body scroll" ref={scrollContainerRef}>
        {liquidations.length === 0 ? (
          <div className="no-records-wrapper">
            {filters && filters.liquidationsMinVolume > FILTER_DEFAULTS.liquidationsMinVolume ? (
              <div className="text-center py-5">
                <p className="text-muted">{t('liquidations.noLiquidationsFiltered', 'No liquidations matching your filter.')}</p>
                <p className="text-muted" style={{fontSize: '13px', marginTop: '8px'}}>
                  {t('liquidations.tryLoweringFilter', 'Try lowering the minimum volume threshold.')}
                </p>
              </div>
            ) : selectedCoins && selectedCoins.length > 0 ? (
              <div className="text-center py-5">
                <p className="text-muted">{t('liquidations.waitingForTrades')}</p>
                <p className="text-muted" style={{fontSize: '13px', marginTop: '8px'}}>
                  <span className="coin-page-link" onClick={() => navigate(localizedPath(`/price/${selectedCoins[0]}`))} style={{color: '#5b9bd5', cursor: 'pointer', textDecoration: 'underline'}}>
                    {t('liquidations.visitCoinPage')}
                  </span>
                </p>
              </div>
            ) : (
              <p className="text-center text-muted py-5">{t('liquidations.noLiquidations')}</p>
            )}
          </div>
        ) : (
          liquidations.map((trade) => {
            const key = `${trade.exchange}-${trade.symbol}-${trade.timestamp}-${trade.price}`;
            return (
              <LiquidationRow
                key={key}
                trade={trade}
                onClick={handleTradeClick}
                t={t}
              />
            );
          })
        )}
      </div>

      {/* Trade Tooltip Popup */}
      {selectedTrade && (
        <TradeTooltip
          trade={selectedTrade}
          livePrice={prices?.[selectedTrade.baseCoin || selectedTrade.symbol?.replace(/USDT$|USD$/i, '')]}
          formatPrice={formatPrice}
          onClose={() => setSelectedTrade(null)}
          onChart={handleLiveChart}
          onShare={handleShare}
          onDetails={handleCoinDetails}
          anchorRect={tooltipAnchor}
        />
      )}

      {/* Share Modal */}
      {shareModalData && (
        <ShareModal
          trade={shareModalData.trade}
          markPrice={shareModalData.markPrice}
          onClose={() => setShareModalData(null)}
        />
      )}

      {/* Liquidation Summary Modal */}
      <LiquidationSummaryModal
        show={showSummary}
        onClose={() => setShowSummary(false)}
        summary={summary}
      />
    </div>
  );
}

export default LiquidationsTable;
